// What a slide's header bar says.
//
// Pulled out of LessonTemplate.js so the rule has one home and can be tested
// without standing up the whole editor: the app header, the canvas header and
// the HTML export all build their bar from `getSlideHeaderParts`.

/** The friendlier wording each BOPPPS section defaults to. */
export const STUDENT_FRIENDLY_TITLES = {
  'bridge-in': 'Getting Started',
  'outcomes': "What You'll Learn Today",
  'pre-assessment': 'Quick Check-In',
  'participatory-learning': "Let's Dive In",
  'post-assessment': 'Your Turn: Show What You Know',
  'summary': 'Key Takeaways',
  'resources': 'Resources & Materials',
};

export const getStudentFriendlyTitle = (section = {}) =>
  section?.studentFriendlyTitle ?? STUDENT_FRIENDLY_TITLES[section?.id] ?? '';

const sameText = (a, b) =>
  Boolean(a) && Boolean(b) && a.trim().toLowerCase() === b.trim().toLowerCase();

const normalizeForCompare = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * The name `repairDeck` used to invent for an unnamed slide: the slide id with
 * its separators turned to spaces and each word capitalised.
 */
const nameDerivedFromSlideId = (slideId) =>
  String(slideId || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Is this stored slide name something a person chose, or something the app
 * generated from the slide's internal id?
 *
 * Decks saved before repairDeck stopped inventing names carry entries like
 * "Section 1 Slide 7" for `section-1-slide-7`. The slide header shows the
 * slide's name as its title, so those decks would show every slide titled with
 * its own id in title case until the author renamed all of them by hand.
 * Treating the generated value as "no name" lets an existing deck fall back to
 * the section title, which is what an unnamed slide should show.
 */
export const isGeneratedSlideName = (name, slideId) => {
  const trimmed = String(name || '').trim();
  if (!trimmed) return true;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('id ')) return true;
  if (!slideId) return false;
  if (trimmed.includes(slideId)) return true;
  return normalizeForCompare(trimmed) === normalizeForCompare(nameDerivedFromSlideId(slideId));
};

/**
 * The slide's name as a person would recognise it — empty when nobody has
 * named it, whatever the stored value says.
 */
export const resolveSlideName = (name, slideId) =>
  (isGeneratedSlideName(name, slideId) ? '' : String(name).trim());

/**
 * Split a slide's header into the one title it shows and the subtitle beneath
 * it.
 *
 * The bar carries the main title exactly once — it used to read
 * "1. Course (CODE) • Course — Slide 2 of 7", which said the same title twice
 * and numbered a slide the drawer thumbnails already number. Now the
 * student-friendly line rides along as a subtitle in its own quieter style, and
 * the position is left to the drawer.
 *
 * Both halves can belong to the slide alone. `slideName` is the slide's own
 * header title and `slideSubtitle` its own subtitle; each falls back to the
 * section's title and student-friendly line only when the slide has none. That
 * is what keeps editing one slide's header from rewriting every other slide in
 * the section, which is what happens when the header is read straight off the
 * section.
 *
 * A named slide owns the header; the section title only reappears in the
 * subtitle when it says something the slide name does not.
 */
export const getSlideHeaderParts = (section = {}, slideName = '', slideSubtitle = '') => {
  const sectionTitle = String(section?.title || '').trim();
  const studentTitle = String(getStudentFriendlyTitle(section) || '').trim();
  const name = String(slideName || '').trim();
  const ownSubtitle = String(slideSubtitle || '').trim();

  const title = name || sectionTitle;

  // A subtitle typed for this slide is the whole subtitle: it replaces the
  // section's line rather than queueing up behind it.
  if (ownSubtitle) return { title, subtitle: ownSubtitle };

  const subtitleParts = [];
  if (studentTitle && !sameText(studentTitle, title)) subtitleParts.push(studentTitle);
  if (name && sectionTitle && !sameText(name, sectionTitle) && !sameText(sectionTitle, studentTitle)) {
    subtitleParts.push(sectionTitle);
  }

  return { title, subtitle: subtitleParts.join(' · ') };
};
